/**
 * @fileoverview Alias/color lookup for OTHER users.
 *
 * `profiles` is own-row only under RLS (it carries email and device
 * fingerprints), so the client resolves anyone else's display identity through
 * `profile_presence`, a table that mirrors id/alias/color of every profile —
 * the one cross-user profile read it has.
 */

import { supabase } from '@/lib/supabase'

export interface ProfilePresence {
  alias: string
  color: string
}

// PostgREST filters travel in the query string; keep each `in()` well under
// the proxy's URL limit for large team rosters.
const PRESENCE_LOOKUP_CHUNK = 100

/**
 * Resolve alias/color for a set of users. Users without a readable presence
 * row are absent from the result; callers decide whether to skip or fall back.
 */
export async function fetchProfilePresence(userIds: readonly string[]): Promise<Map<string, ProfilePresence>> {
  const unique = Array.from(new Set(userIds))
  const result = new Map<string, ProfilePresence>()

  for (let start = 0; start < unique.length; start += PRESENCE_LOOKUP_CHUNK) {
    const chunk = unique.slice(start, start + PRESENCE_LOOKUP_CHUNK)
    const { data, error } = await supabase.from('profile_presence').select('id, alias, color').in('id', chunk)
    if (error) throw error
    for (const row of data ?? []) {
      if (row.id && row.alias !== null && row.color !== null) {
        result.set(row.id, { alias: row.alias, color: row.color })
      }
    }
  }

  return result
}
